// HandbookAcknowledgment.js
// Caretaker Training & Standards Guide forced-read acknowledgment.
// Shown after the Independent Contractor Agreement, before the provider dashboard.
// Checkbox is disabled until user scrolls to the bottom of the content box.
// Writes handbookAcknowledged: true and handbookAcknowledgedAt to Firestore on accept.
// Route: /provider/handbook (ProtectedRoute role="provider")

import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const HANDBOOK_PDF_URL =
  'https://firebasestorage.googleapis.com/v0/b/gravemark-27a30.firebasestorage.app/o/' +
  'Evermark%20Memorial%20Caretaker%20Handbook.pdf?alt=media&token=1cac2fbf-238b-4c98-becf-925ffb09d043';

function HandbookAcknowledgment() {
  const navigate = useNavigate();

  // ===== AUTH =====
  // Store current user from onAuthStateChanged (per CLAUDE.md rule)
  const [currentUser, setCurrentUser] = useState(null);

  // ===== SCROLL DETECTION =====
  const [hasScrolled, setHasScrolled] = useState(false); // true once user reaches the bottom

  // ===== CHECKBOX =====
  const [checked, setChecked] = useState(false); // enabled only after hasScrolled

  // ===== SUBMISSION =====
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  // ===== FADE IN =====
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 50); }, []);

  // ===== AUTH STATE LISTENER =====
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) { navigate('/login'); return; }
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, [navigate]);

  // ===== SCROLL HANDLER =====
  // Fires on scroll events inside the content box.
  // Enables the checkbox once the user reaches within 10px of the bottom.
  const handleScroll = (e) => {
    if (hasScrolled) return; // already unlocked — no need to keep checking
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setHasScrolled(true);
    }
  };

  // ===== ACKNOWLEDGE HANDLER =====
  const handleAgree = async () => {
    if (!checked || saving || !currentUser) return;

    setSaving(true);
    setError('');

    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        handbookAcknowledged:   true,
        handbookAcknowledgedAt: new Date().toISOString(),
      });
      navigate('/provider/dashboard');
    } catch (err) {
      console.error('Handbook acknowledgment error:', err);
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div
      className="ack-page"
      style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease' }}
    >
      <a href="/" className="auth-logo">Evermark Memorial</a>

      <div className="ack-card">

        {/* ===== HEADER ===== */}
        <h2 className="ack-title">Caretaker Training &amp; Standards Guide</h2>
        <p className="ack-subtitle">
          Please read the entire handbook before continuing. Scroll to the bottom to acknowledge.
        </p>

        {/* ===== DOWNLOAD BUTTON ===== */}
        <a
          href={HANDBOOK_PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="ack-download-btn"
        >
          ↓ Download Handbook PDF
        </a>

        {/* ===== SCROLL PROGRESS INDICATOR ===== */}
        <div className={`ack-scroll-indicator${hasScrolled ? ' ack-scroll-indicator--done' : ''}`}>
          {hasScrolled
            ? '✓ You have read the document'
            : 'Scroll to the bottom to enable the checkbox'}
        </div>

        {/* ===== SCROLLABLE CONTENT BOX ===== */}
        {/* onScroll fires on the div — user must reach the bottom to unlock the checkbox */}
        <div className="ack-content-box" onScroll={handleScroll}>

          <p className="ack-content-intro">
            EVERMARK MEMORIAL — CARETAKER TRAINING &amp; STANDARDS GUIDE
          </p>

          {/* ---- Why We Do What We Do ---- */}
          <h3 className="ack-section-heading">WHY WE DO WHAT WE DO</h3>
          <p>
            At Evermark Memorial, we believe that every resting place deserves care, dignity,
            and remembrance. You are more than a caretaker — you are part of a meaningful mission.
          </p>

          {/* ---- Standards of Care ---- */}
          <h3 className="ack-section-heading">STANDARDS OF CARE</h3>
          <ul className="ack-list">
            <li><strong>Respect</strong> — Treat every resting place and family with kindness and compassion</li>
            <li><strong>Dignity</strong> — Uphold the dignity of every loved one</li>
            <li><strong>Attention to Detail</strong> — Carefully inspect, clean, and maintain each site</li>
            <li><strong>Compassion</strong> — Remember your work brings comfort to grieving families</li>
            <li><strong>Integrity</strong> — Be honest, reliable, and accountable</li>
            <li><strong>Preservation</strong> — Protect and preserve the natural and memorial surroundings</li>
          </ul>

          {/* ---- Appearance & Professionalism ---- */}
          <h3 className="ack-section-heading">CARETAKER APPEARANCE &amp; PROFESSIONALISM</h3>
          <p>Wear clean, modest, well-fitting clothing appropriate for outdoor work.</p>
          <ul className="ack-list">
            <li><strong>Required:</strong> Closed-toe shoes, weather-appropriate clothing</li>
            <li><strong>Avoid:</strong> Revealing clothing, open-toed shoes, strong perfumes, offensive graphics</li>
          </ul>

          {/* ---- Communication Guidelines ---- */}
          <h3 className="ack-section-heading">COMMUNICATION GUIDELINES</h3>
          <ul className="ack-list">
            <li><strong>With clients:</strong> Always be kind, patient, and compassionate. Never share personal information or photos without permission.</li>
            <li><strong>With cemetery staff:</strong> Always be polite, professional, and cooperative. Follow all cemetery rules.</li>
            <li>Keep communication professional at all times.</li>
          </ul>

          {/* ---- Gentle Cleaning Process ---- */}
          <h3 className="ack-section-heading">GENTLE CLEANING PROCESS</h3>
          <ol className="ack-list">
            <li><strong>Inspect &amp; Document</strong> — Take clear photos before any work begins</li>
            <li><strong>Remove Loose Debris</strong> — By hand or soft brush only</li>
            <li><strong>Gentle Cleaning</strong> — Clean water and soft brush, light pressure only</li>
            <li><strong>Rinse Carefully</strong> — Clean water, avoid soaking the ground</li>
            <li><strong>Dry &amp; Polish</strong> — Soft cloth only if appropriate for the material</li>
            <li><strong>Tidy the Area</strong> — Leave it better than you found it</li>
            <li><strong>Final Inspection &amp; Documentation</strong> — Take clear after photos</li>
          </ol>

          {/* ---- What Is Not Allowed ---- */}
          <h3 className="ack-section-heading">WHAT IS NOT ALLOWED</h3>
          <ul className="ack-list ack-list--prohibited">
            <li>No harsh chemicals (bleach, ammonia, vinegar, acidic cleaners)</li>
            <li>No pressure washers</li>
            <li>No abrasive tools (wire brushes, steel wool, scrapers)</li>
            <li>No digging or disturbing the ground</li>
            <li>No moving or removing permanent items unless requested</li>
            <li>No painting or restoration work — ever</li>
            <li>No drilling, gluing, or attaching anything to monuments</li>
            <li><strong>NEVER move, lift, reset, repair, or physically alter memorial stones</strong></li>
          </ul>

          {/* ---- Decorating Tips ---- */}
          <h3 className="ack-section-heading">BASIC DECORATING TIPS</h3>
          <p>
            Keep it simple. Use sturdy outdoor items. Place items securely. Always follow
            cemetery guidelines for allowed items and placement. Remove faded or out-of-season items.
          </p>

          {/* ---- Safety & Security ---- */}
          <h3 className="ack-section-heading">SAFETY &amp; SECURITY</h3>
          <ul className="ack-list">
            <li>Be aware of your surroundings at all times</li>
            <li>Park only in designated areas</li>
            <li>Stay hydrated, dress for weather, wear sunscreen</li>
            <li>Do not share client information or photos on social media</li>
            <li>In case of emergency: Call 911, contact cemetery staff, then contact Evermark Memorial</li>
          </ul>

          {/* ---- Cemetery Rules ---- */}
          <h3 className="ack-section-heading">CEMETERY RULES &amp; REGULATIONS</h3>
          <ul className="ack-list">
            <li>Respect the grounds at all times</li>
            <li>Stay on designated paths</li>
            <li>No alcohol, drugs, or smoking on cemetery property</li>
            <li>No personal solicitation</li>
            <li>No pets unless service animals</li>
            <li>No glass containers, open flames, or permanent structures without approval</li>
          </ul>

          {/* ---- Required Documentation ---- */}
          <h3 className="ack-section-heading">REQUIRED DOCUMENTATION FOR EVERY JOB</h3>
          <ol className="ack-list">
            <li>Accept request and read all required info</li>
            <li>Inspect and document the site with photos before starting</li>
            <li>Take clear before AND after photos — required for payment</li>
            <li>Follow the cleaning/decorating guide</li>
            <li>Report any concerns or damage found</li>
            <li>Double check all work is complete</li>
            <li>Submit photos and documentation same day</li>
          </ol>
          <p>
            <strong>Photo guidelines:</strong> Take from standing height, capture whole memorial,
            ensure good lighting, no shadows or obstructions.
          </p>

          {/* ---- Contact ---- */}
          <h3 className="ack-section-heading">CONTACT</h3>
          <p>
            support@evermarkmemorial.com<br />
            Daily from Dawn to Dusk
          </p>

          {/* Bottom sentinel — reaching here means the user has read the document */}
          <div className="ack-content-end">— End of Caretaker Training &amp; Standards Guide —</div>

        </div>
        {/* End scrollable content box */}

        {/* ===== CHECKBOX ===== */}
        {/* Grayed out and pointer-events: none until hasScrolled is true */}
        <label className={`ack-checkbox-label${!hasScrolled ? ' ack-checkbox-label--disabled' : ''}`}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => { if (hasScrolled) setChecked(e.target.checked); }}
            disabled={!hasScrolled}
          />
          <span>
            I have read and understand the Evermark Memorial Caretaker Training &amp; Standards Guide
          </span>
        </label>

        {/* Error */}
        {error && <p className="agreement-error">{error}</p>}

        {/* ===== CONTINUE BUTTON ===== */}
        {/* Disabled until checkbox is checked and not mid-save */}
        <button
          className={`agreement-btn${checked && !saving ? '' : ' agreement-btn--disabled'}`}
          onClick={handleAgree}
          disabled={!checked || saving}
        >
          {saving ? 'Saving...' : 'Continue to Dashboard'}
        </button>

      </div>
      {/* End card */}
    </div>
  );
}

export default HandbookAcknowledgment;
